import { FC, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  RightOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import Main from 'layouts/Main'
import {
  useCancelTrainingBooking,
  useMyTrainingBookings,
  useTrainingInvitations,
  useTrainingList,
  useTrainingRounds,
} from 'hooks/useTraining'
import { MyBookingRow, TrainingEvent } from 'services/interface/eventCalendar/eventCalendar.interface'
import { formatDates, thaiDate } from 'utils/dateFormat'
import { seatLabel, timeRange } from 'utils/trainingFormat'
import { downloadTrainingIcs } from 'utils/trainingIcs'
import TrainingDetailDrawer from 'components/TrainingAnnouncement/TrainingDetailDrawer'
import TrainingInvitationCard from 'components/TrainingAnnouncement/TrainingInvitationCard'
import NeedEmailModal, { useRequireEmail } from 'components/TrainingAnnouncement/NeedEmailModal'

const { Title, Text } = Typography

/** จำนวนรอบที่โชว์ในการ์ดหลักสูตร — เกินกว่านี้บอกเป็น "และอีก N รอบ" */
const ROUNDS_PREVIEW = 3

/** หัวข้อของทุก section ในหน้านี้ — จำนวนอยู่ในป้ายข้างชื่อ ไม่ใช่ประโยคใต้หัวข้อ */
const SectionHeading: FC<{ title: string; count?: number }> = ({ title, count }) => (
  <Space size={8} style={{ display: 'flex', marginBottom: 10 }}>
    <Title level={5} style={{ margin: 0 }}>
      {title}
    </Title>
    {!!count && <Tag>{count}</Tag>}
  </Space>
)

/** การ์ดรอบที่จองไว้ — มีลิงก์ห้องอบรมเพราะจองแล้ว */
const BookingCard: FC<{ booking: MyBookingRow }> = ({ booking }) => {
  const cancelMutation = useCancelTrainingBooking()
  const isPast = !!booking.round_date && booking.round_date < formatDates(new Date())

  const confirmCancel = () =>
    Modal.confirm({
      title: 'ยกเลิกการจอง',
      content: `ยืนยันยกเลิกการจอง ${booking.event_name} รอบ ${thaiDate(booking.round_date)}?`,
      okText: 'ยกเลิกการจอง',
      okButtonProps: { danger: true },
      cancelText: 'ปิด',
      onOk: async () => {
        try {
          await cancelMutation.mutateAsync(booking.id)
          message.success('ยกเลิกการจองเรียบร้อยแล้ว')
        } catch {
          message.error('ยกเลิกการจองไม่สำเร็จ')
        }
      },
    })

  return (
    <Card
      size="small"
      style={{
        height: '100%',
        borderColor: isPast ? undefined : '#b7eb8f',
        background: isPast ? undefined : '#f6ffed',
      }}
    >
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Text strong>{booking.event_name}</Text>
        <Tag color={isPast ? 'default' : 'green'}>{isPast ? 'ผ่านไปแล้ว' : 'จองแล้ว'}</Tag>
      </Space>

      <div style={{ marginTop: 4 }}>
        <Space size={12} wrap>
          <Text>
            <CalendarOutlined /> {thaiDate(booking.round_date)}
          </Text>
          {timeRange(booking.start_time, booking.end_time) && (
            <Text>
              <ClockCircleOutlined /> {timeRange(booking.start_time, booking.end_time)}
            </Text>
          )}
        </Space>
      </div>

      <Text type="secondary" style={{ fontSize: 12 }}>
        {[booking.round_title, booking.instructor && `วิทยากร ${booking.instructor}`]
          .filter(Boolean)
          .join(' · ')}
      </Text>

      <div>
        <Space size={8} wrap style={{ marginTop: 12 }}>
          {booking.meeting_url && !isPast && (
            <Button
              type="primary"
              size="small"
              icon={<VideoCameraOutlined />}
              href={booking.meeting_url}
              target="_blank"
            >
              เข้าห้องอบรม
            </Button>
          )}
          {booking.round_date && !isPast && (
            <Button
              size="small"
              icon={<CalendarOutlined />}
              onClick={() =>
                downloadTrainingIcs({
                  uid: booking.round_id ?? booking.id,
                  title: booking.event_name,
                  date: booking.round_date as string,
                  startTime: booking.start_time,
                  endTime: booking.end_time,
                  url: booking.meeting_url,
                })
              }
            >
              เพิ่มลงปฏิทิน
            </Button>
          )}
          {!isPast && (
            <Button size="small" danger loading={cancelMutation.isPending} onClick={confirmCancel}>
              ยกเลิกการจอง
            </Button>
          )}
        </Space>
      </div>
    </Card>
  )
}

const MyBookingsSection: FC = () => {
  const { data } = useMyTrainingBookings()
  const bookings = data?.data ?? []

  // ไม่มีรอบที่จองไว้ = ซ่อนทั้ง section (ไม่ใช่สถานะว่างที่ต้องอธิบาย — หลักสูตรที่จองได้อยู่ถัดลงไป)
  if (!bookings.length) return null

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeading title="รอบอบรมของฉัน" count={bookings.length} />

      <Row gutter={[16, 16]}>
        {bookings.map(booking => (
          <Col xs={24} xl={12} key={booking.id}>
            <BookingCard booking={booking} />
          </Col>
        ))}
      </Row>
    </section>
  )
}

/**
 * การ์ดหลักสูตรที่เปิดให้จอง — โชว์รอบจริง 3 รอบแรก ให้ตัดสินใจได้โดยไม่ต้องเปิดดูก่อน
 *
 * ponytail: ดึงรอบทีละการ์ด (endpoint เดิม upcoming_only) เพราะ list ส่งมาแค่ rounds_summary
 * ซึ่งยังค้างอยู่ที่รอบที่ผ่านไปแล้ว — ถ้าหน้านี้โตเกินสิบกว่าใบค่อยให้ backend ใส่ rounds
 * มากับ list ทีเดียว (คีย์ cache เดียวกับ drawer อยู่แล้ว กดดูรายละเอียดต่อจึงไม่ยิงซ้ำ)
 */
const CourseCard: FC<{ event: TrainingEvent; onOpenDetail: (id: string) => void }> = ({
  event,
  onOpenDetail,
}) => {
  const { data } = useTrainingRounds(event.id)
  const rounds = data?.data ?? []

  const format = event.training_format
  const isOnsite = format?.training_type === 'Onsite'

  /* ข้อความยาวที่ไม่ได้ใช้ตัดสินใจตอนนี้ — พับไว้ ไม่ให้ดันปุ่มจองลงใต้จอ */
  const extra = [
    event.takeaways && { label: 'สิ่งที่จะได้จากหลักสูตรนี้', value: event.takeaways },
    event.target_audience && { label: 'เหมาะกับใคร', value: event.target_audience },
    isOnsite &&
      (format?.venue_name || format?.venue_address) && {
        label: 'สถานที่จัด',
        value: [format?.venue_name, format?.venue_address].filter(Boolean).join(' '),
        mapUrl: format?.venue_map_url,
      },
    !isOnsite &&
      format?.online_details && { label: 'รายละเอียดออนไลน์', value: format.online_details },
  ].filter(Boolean) as Array<{ label: string; value: string; mapUrl?: string }>

  return (
    <Card size="small" style={{ height: '100%' }}>
      <Space align="start" size={12} style={{ width: '100%' }}>
        {event.cover_image_url && (
          <img
            src={event.cover_image_url}
            alt={event.name}
            loading="lazy"
            style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 8 }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <Space size={4} wrap style={{ marginBottom: 2 }}>
            {event.category && <Tag color="blue">{event.category}</Tag>}
            {event.is_registered && <Tag color="green">จองแล้ว</Tag>}
          </Space>
          <div>
            <Text strong>{event.name}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {[
              event.instructor &&
                `วิทยากร ${event.instructor}${
                  event.instructor_position ? ` (${event.instructor_position})` : ''
                }`,
              format?.training_type &&
                (isOnsite
                  ? `ที่สถานที่จัด${format.venue_name ? ` · ${format.venue_name}` : ''}`
                  : 'ออนไลน์'),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </div>
      </Space>

      {rounds.length > 0 && (
        <ul style={{ margin: '12px 0 0', paddingLeft: 0, listStyle: 'none', fontSize: 12 }}>
          {rounds.slice(0, ROUNDS_PREVIEW).map(round => (
            <li key={round.id} style={{ marginBottom: 6 }}>
              <Space size={8} wrap>
                <Text>{thaiDate(round.round_date)}</Text>
                <Text type="secondary">{timeRange(round.start_time, round.end_time)}</Text>
                {round.title && <Text type="secondary">{round.title}</Text>}
                <Text type={round.is_full ? 'danger' : 'success'}>
                  <TeamOutlined /> {seatLabel(round)}
                </Text>
              </Space>
            </li>
          ))}
          {rounds.length > ROUNDS_PREVIEW && (
            <li>
              <Text type="secondary">และอีก {rounds.length - ROUNDS_PREVIEW} รอบ</Text>
            </li>
          )}
        </ul>
      )}

      {extra.length > 0 && (
        <Collapse
          ghost
          size="small"
          style={{ marginTop: 4 }}
          items={[
            {
              key: 'detail',
              label: <Text style={{ fontSize: 12 }}>รายละเอียดหลักสูตร</Text>,
              children: (
                <Space direction="vertical" size={8}>
                  {extra.map(item => (
                    <div key={item.label}>
                      <Text type="secondary" strong style={{ fontSize: 11 }}>
                        {item.label}
                      </Text>
                      <div style={{ whiteSpace: 'pre-line' }}>
                        <Text style={{ fontSize: 12 }}>{item.value}</Text>
                        {item.mapUrl && (
                          <>
                            {' · '}
                            <a href={item.mapUrl} target="_blank" rel="noreferrer">
                              <EnvironmentOutlined /> เปิดแผนที่
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      )}

      <Button type="primary" block style={{ marginTop: 12 }} onClick={() => onOpenDetail(event.id)}>
        เลือกรอบและจอง <RightOutlined />
      </Button>
    </Card>
  )
}

const OpenTrainingSection: FC<{ onOpenDetail: (id: string) => void }> = ({ onOpenDetail }) => {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useTrainingList({
    has_upcoming_round: true,
    search: search || undefined,
    limit: 20,
    offset: 0,
  })

  const events = data?.data?.data ?? []

  return (
    <section>
      <SectionHeading title="หลักสูตรที่เปิดให้จอง" count={events.length} />

      <Input.Search
        placeholder="ค้นหาชื่อหลักสูตร"
        allowClear
        onSearch={setSearch}
        style={{ maxWidth: 360, marginBottom: 16 }}
      />

      {isLoading ? (
        <Spin />
      ) : !events.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="ยังไม่มีรอบอบรมที่เปิดรับในขณะนี้ ทีมงานจะแจ้งเมื่อมีรอบใหม่"
        />
      ) : (
        <Row gutter={[16, 16]}>
          {events.map(event => (
            <Col xs={24} xl={12} key={event.id}>
              <CourseCard event={event} onOpenDetail={onOpenDetail} />
            </Col>
          ))}
        </Row>
      )}
    </section>
  )
}

/**
 * คำเชิญที่ยังรอตอบ — อยู่บนสุดของหน้า เพราะเป็นสิ่งเดียวที่รอเจ้าตัวลงมือ
 *
 * คำเชิญถูกออกให้ใบสมัคร (ลีด) ตั้งแต่ก่อนร้านถูกอนุมัติ backend สืบกลับให้ผ่าน
 * `factory_registration_form.factory_id` — ร้านที่ไม่ได้มาจากลีดได้ [] แล้วทั้ง section
 * หายไปเงียบ ๆ ซึ่งถูกต้อง (ไม่ใช่สถานะว่างที่ต้องอธิบาย)
 *
 * error ก็ซ่อนทั้ง section เหมือนกัน: คำเชิญเป็นของเสริมของหน้านี้ ถ้าเส้นนี้ล่มต้องไม่
 * ไปบังรายการหลักสูตรซึ่งยังจองได้ตามปกติ
 */
const InvitationSection: FC<{ onOpenDetail: (eventId: string) => void }> = ({ onOpenDetail }) => {
  const { data } = useTrainingInvitations()
  const { ensureEmail, needEmailOpen, openNeedEmail, closeNeedEmail } = useRequireEmail()

  const invitations = data?.data ?? []
  if (!invitations.length) return null

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeading title="คำเชิญของฉัน" count={invitations.length} />

      <Row gutter={[16, 16]}>
        {invitations.map(invitation => (
          <Col xs={24} xl={12} key={invitation.id}>
            <TrainingInvitationCard
              invitation={invitation}
              ensureEmail={ensureEmail}
              onNeedEmail={openNeedEmail}
              onOpenDetail={onOpenDetail}
            />
          </Col>
        ))}
      </Row>

      <NeedEmailModal open={needEmailOpen} onClose={closeNeedEmail} />
    </section>
  )
}

const TrainingPage: FC = () => {
  const [detailEventId, setDetailEventId] = useState<string | undefined>()

  return (
    <Main namePage="การอบรม">
      <div className="training-page">
        <Title level={4}>การอบรม</Title>

        <InvitationSection onOpenDetail={setDetailEventId} />
        <MyBookingsSection />
        <OpenTrainingSection onOpenDetail={setDetailEventId} />

        <TrainingDetailDrawer
          eventId={detailEventId}
          open={!!detailEventId}
          onClose={() => setDetailEventId(undefined)}
        />
      </div>
    </Main>
  )
}

export default TrainingPage
